import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../../ui/button";
import { ArrowLeft, Pencil, Loader2 } from "lucide-react";
import { EmailPreviewIframe } from "../components/email/EmailPreviewIframe";
import { epicengageSupabasev1 } from "../../../../integrations/supabase/client";
import { useToast } from "../../../../hooks/use-toast";

type Template = {
  id: string;
  name: string | null;
  subject_line: string | null;
  body_html: string | null;
  cta_text: string | null;
  cta_url: string | null;
};

const TemplatePreview = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [tpl, setTpl] = useState<Template | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      const { data, error } = await epicengageSupabasev1
        .from("email_templates")
        .select("id, name, subject_line, body_html, cta_text, cta_url")
        .eq("id", id)
        .maybeSingle();
      if (error) {
        toast({ title: "Failed to load template", description: error.message, variant: "destructive" });
      } else {
        setTpl(data as Template);
      }
      setLoading(false);
    })();
  }, [id, toast]);

  const html = useMemo(() => {
    if (!tpl) return "";
    const ctaHtml =
      tpl.cta_text && tpl.cta_url
        ? `<p style="margin-top:24px;"><a href="${tpl.cta_url}" style="display:inline-block;background:#111;color:#fff;padding:10px 18px;border-radius:6px;text-decoration:none;font-weight:500;">${tpl.cta_text}</a></p>`
        : "";
    return `${tpl.body_html ?? ""}${ctaHtml}`;
  }, [tpl]);

  return (
      <div className="px-10 py-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-start gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/clientpanel/enagev1//templates")}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">{tpl?.name || "Preview"}</h1>
              <p className="text-sm text-muted-foreground mt-1">Subject: {tpl?.subject_line || "—"}</p>
            </div>
          </div>
          {tpl && (
            <Button variant="outline" onClick={() => navigate(`/clientpanel/enagev1//templates/${tpl.id}/edit`)}>
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <EmailPreviewIframe html={html} minHeight={600} />
        )}
      </div>
  );
};

export default TemplatePreview;
